/**
 * Version history — §7.5: "Version list, view-at-version, restore, attribution."
 *
 * And nothing else. §7.5 rules out branches, merges, and cross-decade blame in
 * the same sentence, so this screen has no notion of a branch to offer and no
 * place to grow one.
 *
 * Three things here are easy to render into a lie, so each is stated rather than
 * implied:
 *
 * - **Restore does not publish.** It writes the version's content into the
 *   working draft and snapshots that. `published_version_id` is untouched, so
 *   readers keep seeing exactly what they were seeing until someone publishes
 *   (§7.4). A confirmation that says "restore this version" without saying that
 *   is describing a different feature.
 * - **View-at-version shows markdown, not the editor.** The editor is bound to
 *   the live collaborative document for this node; pointing it at a historical
 *   body would show the current one wearing an old label. §5.1 makes markdown
 *   the derived-but-faithful rendering, and it is what the diff below is
 *   computed from, so the two panes agree by construction.
 * - **The newest version can still change.** A save coalesces into the author's
 *   open burst (§7.5's debouncing), so the top row is not immutable the way the
 *   rest of the list is.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  useCreateVersion,
  useDocument,
  useDocumentDiff,
  useDocumentVersion,
  useDocumentVersions,
  useRestoreVersion,
} from '../../api/queries.ts';
import type { DiffRevisionRef, DocumentVersionListItem } from '../../api/types.ts';
import { formatDateTime, formatRelative } from '../../components/format.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import {
  Alert,
  EmptyState,
  ErrorState,
  Loading,
  PageError,
} from '../../components/ui/feedback.tsx';
import { Select } from '../../components/ui/field.tsx';
import { Card, CardHeader, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';
import { DiffHeading, DiffView } from './diff.tsx';
import { useDocumentNodeId } from './navigation.tsx';

const PAGE = 50;

/** `''` means "let the API choose" — its defaults are the question people ask. */
type RefChoice = '' | DiffRevisionRef;

function VersionRow({
  version,
  selected,
  onSelect,
  onRestore,
}: {
  version: DocumentVersionListItem;
  selected: boolean;
  onSelect: () => void;
  onRestore: () => void;
}) {
  return (
    <TR className={selected ? 'bg-secondary' : undefined}>
      <TD>
        <button
          type="button"
          onClick={onSelect}
          className="link cursor-pointer font-mono text-sm font-semibold"
        >
          v{version.versionNumber}
        </button>
        {version.published ? (
          <Badge tone="success" className="ml-2">
            published
          </Badge>
        ) : null}
      </TD>
      <TD>
        {/* §7.5 — attribution. `authorName` is null only for a person row that
            has since gone; the id is still the truth, so say so rather than
            printing nothing. */}
        {version.authorName ?? <span className="text-tertiary">Unknown author</span>}
      </TD>
      <TD>
        <span title={formatDateTime(version.createdAt)}>{formatRelative(version.createdAt)}</span>
      </TD>
      <TD className="text-right">
        <Button size="sm" variant="tertiary" onPress={onRestore}>
          Restore
        </Button>
      </TD>
    </TR>
  );
}

function RestoreDialog({
  nodeId,
  version,
  onClose,
}: {
  nodeId: string;
  version: DocumentVersionListItem;
  onClose: () => void;
}) {
  const restore = useRestoreVersion(nodeId);

  return (
    <>
      <DialogBody>
        <Alert tone="info" title="This does not change what readers see">
          The content of v{version.versionNumber} becomes the working draft, and the restore is
          itself snapshotted so the history records that it happened.{' '}
          <span className="font-semibold">
            The published version is untouched until someone publishes.
          </span>
        </Alert>
        {restore.isError ? <ErrorState error={restore.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={restore.isPending}
          onPress={() => restore.mutate(version.id, { onSuccess: onClose })}
        >
          Restore v{version.versionNumber} into the draft
        </Button>
      </DialogFooter>
    </>
  );
}

function VersionBody({ nodeId, versionId }: { nodeId: string; versionId: string }) {
  const query = useDocumentVersion(nodeId, versionId);

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorState error={query.error} />;

  const { version, body } = query.data;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-tertiary">
        v{version.versionNumber} by {version.authorName ?? 'an unknown author'},{' '}
        {formatDateTime(version.createdAt)}.
      </p>
      <pre className="overflow-x-auto rounded-lg border border-secondary bg-secondary p-4 font-mono text-xs whitespace-pre-wrap text-secondary">
        {body.markdown}
      </pre>
    </div>
  );
}

function DiffPanel({ nodeId, versions }: { nodeId: string; versions: DocumentVersionListItem[] }) {
  const [from, setFrom] = useState<RefChoice>('');
  const [to, setTo] = useState<RefChoice>('');

  const query = useDocumentDiff(nodeId, {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  });

  const options = [
    { value: '' as RefChoice, label: 'Default', description: 'What the API picks' },
    { value: 'published' as RefChoice, label: 'Published version' },
    { value: 'draft' as RefChoice, label: 'Working draft' },
    ...versions.map((version) => ({
      value: version.id as RefChoice,
      label: `v${version.versionNumber}`,
      description: `${version.authorName ?? 'unknown'} · ${formatRelative(version.createdAt)}`,
    })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Select label="From" options={options} selectedKey={from} onSelectionChange={setFrom} />
        <Select label="To" options={options} selectedKey={to} onSelectionChange={setTo} />
      </div>
      {query.isPending ? <Loading /> : null}
      {query.isError ? <ErrorState error={query.error} /> : null}
      {query.data ? (
        <>
          <DiffHeading response={query.data} />
          <DiffView diff={query.data.diff} />
        </>
      ) : null}
    </div>
  );
}

export function DocumentVersionsPage() {
  const nodeId = useDocumentNodeId();
  const parent = useDocument(nodeId);
  const [limit, setLimit] = useState(PAGE);
  const versions = useDocumentVersions(nodeId, { limit });
  const snapshot = useCreateVersion(nodeId);

  const [selected, setSelected] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<DocumentVersionListItem | null>(null);
  const [pane, setPane] = useState<'diff' | 'version'>('diff');

  if (parent.isError) {
    return (
      <PageError
        error={parent.error}
        title="Version history"
        back={{ href: '/schema/spaces', label: 'Back to spaces' }}
      />
    );
  }

  const rows = versions.data?.versions ?? [];
  const total = versions.data?.total ?? 0;

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/documents/$nodeId" params={{ nodeId }} className="text-tertiary no-underline">
            ← {parent.data?.document.title ?? 'Document'}
          </Link>
        }
        title="Version history"
        description="Snapshot on save with debouncing, or at session end. Every version for 30 days, then daily, then monthly."
        actions={
          <Button
            variant="secondary"
            isPending={snapshot.isPending}
            onPress={() => snapshot.mutate()}
          >
            Snapshot now
          </Button>
        }
      />

      {snapshot.data ? (
        <Alert tone={snapshot.data.created ? 'success' : 'info'}>
          {snapshot.data.created
            ? `Wrote v${snapshot.data.version.versionNumber}.`
            : 'Nothing to snapshot — the draft is identical to the newest version.'}
        </Alert>
      ) : null}
      {snapshot.isError ? <ErrorState error={snapshot.error} /> : null}

      <div className="grid gap-6 lg:grid-cols-[3fr_4fr]">
        <Card padded={false}>
          <CardHeader title="Versions" description={total > 0 ? `${total} in all` : undefined} />
          {versions.isPending ? (
            <Loading />
          ) : versions.isError ? (
            <div className="p-5">
              <ErrorState error={versions.error} />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="No versions yet"
              description="A version is written the first time this document's body is saved."
            />
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH>Version</TH>
                    <TH>Author</TH>
                    <TH>Written</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {rows.map((version) => (
                    <VersionRow
                      key={version.id}
                      version={version}
                      selected={selected === version.id}
                      onSelect={() => {
                        setSelected(version.id);
                        setPane('version');
                      }}
                      onRestore={() => setRestoring(version)}
                    />
                  ))}
                </TBody>
              </Table>
              {rows.length < total ? (
                <div className="border-t border-secondary px-4 py-3">
                  <Button size="sm" variant="tertiary" onPress={() => setLimit(limit + PAGE)}>
                    Show older versions
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Card>

        <Card padded={false}>
          <CardHeader
            title={pane === 'diff' ? 'What changed' : 'At this version'}
            actions={
              <div className="flex items-center gap-1 rounded-lg border border-primary p-1">
                <Button
                  size="sm"
                  variant={pane === 'diff' ? 'primary' : 'tertiary'}
                  onPress={() => setPane('diff')}
                >
                  Diff
                </Button>
                <Button
                  size="sm"
                  variant={pane === 'version' ? 'primary' : 'tertiary'}
                  onPress={() => setPane('version')}
                  isDisabled={selected === null}
                >
                  Body
                </Button>
              </div>
            }
          />
          <div className="p-5">
            {pane === 'diff' ? (
              <DiffPanel nodeId={nodeId} versions={rows} />
            ) : selected ? (
              <VersionBody nodeId={nodeId} versionId={selected} />
            ) : (
              <EmptyState
                title="Pick a version"
                description="Choose one on the left to read it as it was written."
              />
            )}
          </div>
        </Card>
      </div>

      <Dialog
        title={restoring ? `Restore v${restoring.versionNumber}` : 'Restore'}
        isOpen={restoring !== null}
        onOpenChange={(open) => {
          if (!open) setRestoring(null);
        }}
      >
        {(close) =>
          restoring ? (
            <RestoreDialog
              nodeId={nodeId}
              version={restoring}
              onClose={() => {
                setRestoring(null);
                close();
              }}
            />
          ) : null
        }
      </Dialog>
    </>
  );
}
